import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TrackByFunction
} from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngxs/store";
import * as TagsActions from "@vp/data-access/tags";
import { UserStateService } from "@vp/data-access/users";
import { Tag, TagType } from "@vp/models";
import { filterNullMap } from "@vp/shared/operators";
import * as TagManagerActions from "@vp/tag-manager/data-access/tag-manager-state";
import {
  TagManagerActionsService,
  tagManagerDefaultState,
  TagManagerState
} from "@vp/tag-manager/data-access/tag-manager-state";
import { AddTagDialogComponent } from "@vp/tag-manager/ui/add-tag";
import { Operation } from "rfc6902";
import { Observable, Subject } from "rxjs";
import { concatMap, distinctUntilChanged, map, switchMap, take } from "rxjs/operators";

@Component({
  selector: "vp-tag-manager-shell",
  templateUrl: "./tag-manager-shell.component.html",
  styleUrls: ["./tag-manager-shell.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TagManagerActionsService, UserStateService],
  standalone: false
})
export class TagManagerShellComponent implements OnInit, OnDestroy {
  private readonly matDialog = inject(MatDialog);
  private readonly tagManagerActionsService = inject(TagManagerActionsService);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);

  tagTypeSearch$: Observable<string> = inject(Store).select(TagManagerState.tagTypeSearch);
  tagSearch$: Observable<string> = inject(Store).select(TagManagerState.tagSearch);
  attention$: Observable<Record<string, boolean>> = inject(Store).select(TagManagerState.attention);
  pendingOperations$: Observable<Operation[]> = inject(Store).select(TagManagerState.operations);
  selectedTagType$: Observable<TagType | null> = inject(Store).select(
    TagManagerState.selectedTagType
  );
  uiState$ = inject(Store).select(TagManagerState.getUiState) as Observable<
    (filter: string) => boolean | string
  >;

  private readonly destroy$ = new Subject<void>();

  get showPreview$() {
    return this.uiState$.pipe(map(filterFn => filterFn("showPreview")));
  }

  showActionRow$ = this.pendingOperations$.pipe(map(o => o.length > 0));
  isTagSelected$ = this.selectedTagType$.pipe(map(selectedTag => selectedTag));

  ngOnInit(): void {
    this.tagManagerActionsService.listen();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(new TagManagerActions.UpdateState(tagManagerDefaultState));
  }

  // TODO: Abstract this into a directive
  attentionTo = (what: string): Observable<boolean> => {
    return this.attention$.pipe(
      map(attention => attention[what]),
      distinctUntilChanged()
    );
  };

  closePreview = () => {
    this.store.dispatch(
      new TagManagerActions.PatchUiState({
        showPreview: false
      })
    );
  };

  addTag = () => {
    this.selectedTagType$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tagType: TagType) =>
          this.matDialog
            .open(AddTagDialogComponent, {
              disableClose: true,
              width: "30vw",
              data: {
                tagTypeId: tagType.tagTypeId
              },
              panelClass: "mat-mdc-dialog-basic"
            } as MatDialogConfig)
            .afterClosed()
            .pipe(filterNullMap())
        ),
        concatMap((tag: Partial<Tag>) => this.store.dispatch(new TagManagerActions.AddTag(tag)))
      )
      .subscribe(() => {
        const tagFilter = this.route.snapshot.data?.tagFilter;
        if (tagFilter) {
          this.store.dispatch(new TagsActions.SetFiltered(tagFilter, false));
        } else {
          this.store.dispatch(new TagsActions.LoadTags(false));
        }
      });
  };

  trackTagById: TrackByFunction<Tag> = (_: number, item: Tag) => item.tagId;
}
